import classNames from 'classnames';
import { Home24, UserFilled24 } from '@carbon/icons-react';
import { useStore } from 'effector-react';
import { FC, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ENTITIES } from 'routes/constants';
import { $legalEntitiesStore } from 'store/entities';
import { $userStore } from 'store/user';
import { Hidden } from 'react-grid-system';
import { setProfileTypeEV } from 'store/main/events';
import { ProfileTypeEnum } from 'store/main/interface';
import { clearApplicationsQueryParamsEV } from 'store/applications/events';
import { clearCommunicationQueryParamsEV, createQueryParamsEV } from 'store/communication/events';
import { clearDelegationsQueryParamsEV } from 'store/delegations/events';
import { clearHistoryQueryParamsEV } from 'store/history/events';
import { setAccountUuidEV } from 'store/user/events';
import { $partnerOrganizationStore } from '../../../store/partnerOrganizations';
import { useProfile } from '../../../hooks/useProfile';

interface ScrollHeaderProps {
  refHeader: React.MutableRefObject<HTMLDivElement | null>;
}

export const ScrollHeader: FC<ScrollHeaderProps> = ({ refHeader }) => {
  const user = useStore($userStore);
  const history = useHistory();
  const { type: profileType, data: profile } = useProfile();
  const { profile: legalEntityProfile } = useStore($legalEntitiesStore);
  const { profile: partnerOrganizationProfile } = useStore($partnerOrganizationStore);
  const selected_uuid = user?.accounts?.selected_uuid;

  const entity = useMemo(() => {
    const find = (v: any) => v.uuid === selected_uuid;

    if (profileType === ProfileTypeEnum.LEGAL_ENTITY) {
      return user?.accounts?.legal_entities?.find(find);
    }

    if (profileType === ProfileTypeEnum.PARTNER_ORGANIZATION) {
      return user?.accounts?.partner_organizations?.find(find);
    }

    return user?.accounts?.natural_person;
  }, [profileType, selected_uuid, user]);

  const name = useMemo(() => {
    if (profileType === ProfileTypeEnum.LEGAL_ENTITY) {
      return `${user?.name} (${legalEntityProfile?.legal_entity_name})`;
    }

    if (
      profileType === ProfileTypeEnum.PARTNER_ORGANIZATION ||
      profile.return_profile?.type === ProfileTypeEnum.PARTNER_ORGANIZATION
    ) {
      return `${user?.name} (${partnerOrganizationProfile?.organization_name})`;
    }

    return entity?.name;
  }, [profileType, user, entity, legalEntityProfile]);

  const handleHomeClick = () => {
    history.block()();

    if (localStorage.getItem('token')) {
      setAccountUuidEV(user?.accounts?.natural_person?.uuid || '');
      setProfileTypeEV(ProfileTypeEnum.PERSON);
      createQueryParamsEV(undefined);
      clearApplicationsQueryParamsEV(undefined);
      clearHistoryQueryParamsEV(undefined);
      clearDelegationsQueryParamsEV(undefined);
      clearCommunicationQueryParamsEV(undefined);
      history.push(ENTITIES.LIST);
    } else {
      history.push('/');
    }
  };

  return (
    <nav className="web-epak-header-mini" ref={refHeader}>
      <div className="web-epak-header__wrap">
        <Hidden xs>
          <div className="web-epak-header-mini__home">
            <Link
              to={localStorage.getItem('token') ? ENTITIES.LIST : '/'}
              onClick={handleHomeClick}
            >
              <Home24 />
            </Link>
            {process.env.REACT_APP_ENVIRONMENT_MODE !== 'Production' && (
              <div className={classNames('environment')}>
                {process.env.REACT_APP_ENVIRONMENT_MODE}
              </div>
            )}
          </div>
          {user && (
            <div className="web-epak-header-mini__block">
              <span>
                <UserFilled24 />
                {name}
              </span>
            </div>
          )}
        </Hidden>
      </div>
    </nav>
  );
};
